
import React from 'react';
import Layout from '@/components/layout/Layout';
import { Shield, Lock, Users, Eye, FileCheck, AlertTriangle, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const Security = () => {
  return (
    <Layout>
      <div className="py-12 px-4 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Shield className="text-primary" size={32} />
              </div>
            </div>
            <h1 className="text-4xl font-bold mb-4">Security & Privacy</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              At Pink Paradise, your safety, security, and privacy are our highest priorities. 
              Learn how we protect you and your data.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-card rounded-xl p-8 shadow-sm border">
              <Lock className="text-primary mb-4" size={32} />
              <h2 className="text-2xl font-semibold mb-3">Data Protection</h2>
              <p className="text-muted-foreground mb-5">
                We employ industry-leading encryption and security protocols to ensure your personal data 
                and communications remain completely private and secure.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <ChevronRight className="text-primary shrink-0 mt-1" size={16} />
                  <span>End-to-end encryption for all messages</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="text-primary shrink-0 mt-1" size={16} />
                  <span>Secure payment processing with discreet billing</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="text-primary shrink-0 mt-1" size={16} />
                  <span>Zero activity logging outside of essential functions</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="text-primary shrink-0 mt-1" size={16} />
                  <span>Advanced encryption for stored personal information</span>
                </li>
              </ul>
            </div>

            <div className="bg-card rounded-xl p-8 shadow-sm border">
              <Users className="text-primary mb-4" size={32} />
              <h2 className="text-2xl font-semibold mb-3">Identity Verification</h2>
              <p className="text-muted-foreground mb-5">
                Our comprehensive verification system ensures that all escorts on our platform 
                are genuine, creating a safer environment for all users.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <ChevronRight className="text-primary shrink-0 mt-1" size={16} />
                  <span>Multi-step identity verification process</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="text-primary shrink-0 mt-1" size={16} />
                  <span>Document verification conducted by security specialists</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="text-primary shrink-0 mt-1" size={16} />
                  <span>Photo verification to prevent catfishing</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="text-primary shrink-0 mt-1" size={16} />
                  <span>Verified badges display on authentic profiles</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-card rounded-xl p-8 border mb-12">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <Eye className="text-primary" size={24} />
              Privacy Features
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="p-4 bg-muted/50 rounded-lg">
                <h3 className="font-medium mb-2">Anonymous Browsing</h3>
                <p className="text-sm text-muted-foreground">
                  Browse profiles without revealing your identity until you're ready to connect.
                </p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <h3 className="font-medium mb-2">Discreet Messaging</h3>
                <p className="text-sm text-muted-foreground">
                  Our messaging system keeps your communications private and secure.
                </p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <h3 className="font-medium mb-2">Privacy Controls</h3>
                <p className="text-sm text-muted-foreground">
                  Customize exactly what information is visible to others.
                </p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <h3 className="font-medium mb-2">Discreet Billing</h3>
                <p className="text-sm text-muted-foreground">
                  All charges appear with a discrete business name on statements.
                </p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <h3 className="font-medium mb-2">Data Deletion</h3>
                <p className="text-sm text-muted-foreground">
                  Request complete deletion of your account and data at any time.
                </p>
              </div>
              <div className="p-4 bg-muted/50 rounded-lg">
                <h3 className="font-medium mb-2">No Tracking</h3>
                <p className="text-sm text-muted-foreground">
                  We don't track your activity or share data with third parties.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-xl p-8 mb-12">
            <div className="flex items-start gap-4">
              <AlertTriangle className="text-amber-500 shrink-0" size={32} />
              <div>
                <h2 className="text-2xl font-semibold mb-3">Safety Tips</h2>
                <p className="text-muted-foreground mb-4">
                  While we work hard to create a secure platform, please follow these safety guidelines:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <ChevronRight className="text-amber-500 shrink-0 mt-1" size={16} />
                    <span>Always meet in public places for initial meetings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="text-amber-500 shrink-0 mt-1" size={16} />
                    <span>Inform a trusted friend about your plans</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="text-amber-500 shrink-0 mt-1" size={16} />
                    <span>Trust your instincts - if something feels wrong, leave</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="text-amber-500 shrink-0 mt-1" size={16} />
                    <span>Keep communications on our platform until you feel comfortable</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="text-amber-500 shrink-0 mt-1" size={16} />
                    <span>Report any suspicious behavior immediately</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl p-8 border mb-12">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <FileCheck className="text-primary" size={24} />
              Our Security Commitments
            </h2>
            <div className="space-y-6">
              <p className="text-muted-foreground">
                We are committed to maintaining the highest standards of security and privacy. Our platform undergoes:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="p-5 border rounded-lg">
                  <h3 className="font-medium mb-2">Regular Security Audits</h3>
                  <p className="text-sm text-muted-foreground">
                    Independent security experts regularly test our systems for vulnerabilities.
                  </p>
                </div>
                <div className="p-5 border rounded-lg">
                  <h3 className="font-medium mb-2">Data Protection Reviews</h3>
                  <p className="text-sm text-muted-foreground">
                    Our data protection practices are regularly reviewed and updated.
                  </p>
                </div>
                <div className="p-5 border rounded-lg">
                  <h3 className="font-medium mb-2">Staff Training</h3>
                  <p className="text-sm text-muted-foreground">
                    All team members receive regular training on security protocols.
                  </p>
                </div>
                <div className="p-5 border rounded-lg">
                  <h3 className="font-medium mb-2">Incident Response Plan</h3>
                  <p className="text-sm text-muted-foreground">
                    We maintain a comprehensive plan for responding to security incidents.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-muted-foreground mb-6">
              Have concerns or questions about security and privacy?
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg">
                <Link to="/support">Contact Support</Link>
              </Button>
              <Button variant="outline" asChild size="lg">
                <Link to="/faq">View FAQs</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Security;
